<script setup>
// Bandeaux de l'abonnement en haut de l'application (VIG-450, maquettes NG_Essai,
// NG_MesDossiers, FinEssaiC1, FinEssaiCollab) :
// - compteur de l'essai gratuit ;
// - bandeau À l'usage « Voir les formules », masquable par chaque utilisateur ;
// - fenêtre de fin d'essai, vue une fois par chaque membre à la première page ouverte
//   après la fin de l'essai.
// Bandeau et fenêtre sont montrés à tout l'office (B4.1 bis, B4.2, 22/10/2026) ; seul le
// Notaire Administrateur (`canManage`) souscrit : les autres membres sont renvoyés vers lui.
// Un seul bandeau à la fois ; aucun bandeau commercial ni fenêtre sur les pages de
// l'abonnement (Mon abonnement, Souscrire, Changer de formule, Confirmation), où le
// compteur d'essai reste affiché sans son lien.
import {computed, onMounted, onUnmounted, ref} from 'vue';
import {router, usePage, Link} from '@inertiajs/vue3';
import IncludedFeatures from './IncludedFeatures.vue';
import {euros, longDate, perMonth} from './format.js';

const DISMISS_KEY = 'vigiacte.subscription-offer-banner.dismissed';
// Le compteur ajoute la date de fin à 1 dossier restant ou à 3 jours au plus.
const TRIAL_DATE_FROM_DOSSIERS = 1;
const TRIAL_DATE_FROM_DAYS = 3;

const page = usePage();
const subscription = computed(() => page.props.subscription);
const trial = computed(() => subscription.value?.trial);
const annualFirstNotairePrice = computed(() => subscription.value?.pricing?.plans?.annuel?.firstNotairePrice ?? 0);
const canManage = computed(() => !!subscription.value?.canManage);
const administratorName = computed(() => subscription.value?.administratorName);

const onSubscriptionPage = computed(() => {
  try {
    return route().current('subscription.*');
  } catch {
    return false;
  }
});

const showTrialCounter = computed(() => subscription.value?.status === 'trial' && canManage.value);
const showTrialEndDate = computed(() => trial.value
    && (trial.value.dossiersLeft <= TRIAL_DATE_FROM_DOSSIERS || trial.value.daysLeft <= TRIAL_DATE_FROM_DAYS));

const readDismissed = () => {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};
const offerDismissed = ref(readDismissed());
const showOfferBanner = computed(() => subscription.value?.status === 'starter'
    && !offerDismissed.value
    && !onSubscriptionPage.value
    && !showTrialEndNotice.value);

const dismissOffer = () => {
  offerDismissed.value = true;
  try {
    window.localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    // Stockage indisponible (navigation privée) : le bandeau réapparaîtra au prochain chargement.
  }
};

const trialEndNoticeClosed = ref(false);
const showTrialEndNotice = computed(() => !!subscription.value?.showTrialEndNotice
    && !trialEndNoticeClosed.value
    && !onSubscriptionPage.value);

const closeTrialEndNotice = (then = null) => {
  trialEndNoticeClosed.value = true;
  router.post(route('subscription.trial-end-notice.dismiss'), {}, {
    preserveScroll: true,
    preserveState: true,
    onFinish: () => then?.(),
  });
};

// « Passer en illimité » : page Souscrire, Annuel présélectionné et nombre minimal de notaires.
const subscribeFromNotice = () => {
  closeTrialEndNotice(() => router.visit(route('subscription.create')));
};
const plansFromNotice = () => {
  closeTrialEndNotice(() => router.visit(route('subscription.show')));
};

const closeOnEscape = (event) => {
  if (event.key === 'Escape' && showTrialEndNotice.value) {
    closeTrialEndNotice();
  }
};
onMounted(() => document.addEventListener('keydown', closeOnEscape));
onUnmounted(() => document.removeEventListener('keydown', closeOnEscape));
</script>

<template>
  <div v-if="subscription">
    <!-- Compteur de l'essai -->
    <div v-if="showTrialCounter && trial" class="border-b border-green-50 bg-green-10">
      <div class="mx-auto flex max-w-screen-3xl flex-wrap items-center justify-center gap-x-4 gap-y-1 px-4 py-2 text-sm">
        <span class="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-bold text-white">Essai gratuit</span>
        <span class="text-gray-700">
          <strong>{{ trial.dossiersLeft }} {{ trial.dossiersLeft > 1 ? 'dossiers restants' : 'dossier restant' }}</strong>
          sur {{ trial.dossiersIncluded }} · <strong>{{ trial.daysLeft }} {{ trial.daysLeft > 1 ? 'jours restants' : 'jour restant' }}</strong>
          <template v-if="showTrialEndDate"> · jusqu'au {{ longDate(trial.endsAt, {withYear: false}) }}</template>
        </span>
        <Link v-if="canManage && !onSubscriptionPage" :href="route('subscription.show')"
              class="font-semibold text-green-200 underline hover:text-green-100">
          Choisir ma formule
        </Link>
      </div>
    </div>

    <!-- Bandeau À l'usage (tout l'office, B4.2) -->
    <div v-if="showOfferBanner" class="border-b border-green-50 bg-green-10">
      <div class="mx-auto flex max-w-screen-3xl flex-wrap items-center justify-center gap-x-4 gap-y-1 px-4 py-2 text-sm text-gray-700">
        <span v-if="canManage" class="inline-flex flex-col items-center text-center">
          La LCB-FT illimitée pour tout l'office, à partir de {{ euros(annualFirstNotairePrice) }}&nbsp;€ HT par an en formule Annuel.
          <span class="text-xs text-gray-500">soit {{ euros(perMonth(annualFirstNotairePrice)) }}&nbsp;€ HT par mois</span>
        </span>
        <span v-else class="text-center">
          Pour des dossiers illimités, parlez-en à <strong>{{ administratorName || 'votre Notaire Administrateur' }}</strong>.
        </span>
        <Link :href="route('subscription.show')" class="font-semibold text-green-200 underline hover:text-green-100">
          Voir les formules
        </Link>
        <button type="button" class="ml-2 text-gray-400 hover:text-gray-700" aria-label="Masquer ce bandeau" @click="dismissOffer">×</button>
      </div>
    </div>

    <!-- Fin d'essai (tout l'office, B4.1 et B4.1 bis) -->
    <teleport to="body">
      <transition enter-active-class="ease-out duration-300" enter-from-class="opacity-0" enter-to-class="opacity-100"
                  leave-active-class="ease-in duration-200" leave-from-class="opacity-100" leave-to-class="opacity-0">
        <div v-if="showTrialEndNotice"
             class="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-gray-900/55 p-4"
             @click.self="closeTrialEndNotice()">
          <div role="dialog" aria-modal="true" aria-labelledby="trial-end-title"
               class="relative w-full max-w-[820px] overflow-hidden rounded-[18px] bg-white shadow-[0_24px_60px_rgba(0,0,0,.35)]">
            <button type="button" class="absolute right-4 top-3 text-xl leading-none text-white/80 hover:text-white"
                    aria-label="Fermer" @click="closeTrialEndNotice()">×</button>

            <div class="bg-gradient-to-br from-[#25B6A5] to-[#1C9F90] px-6 pb-6 pt-7 text-center text-white sm:px-11">
              <p class="mb-2 text-[13px] font-semibold text-[#CFF5EE]">
                {{ canManage ? 'Bravo, vous avez terminé votre essai !' : 'Bravo, votre office a terminé son essai gratuit !' }}
              </p>
              <h2 id="trial-end-title" class="text-2xl font-extrabold leading-tight sm:text-[26px]">Toute votre LCB-FT sur une seule plateforme.</h2>
              <p class="mt-2.5 text-[15px]">Soyez serein pour l'inspection. Faites comme les +3 000 offices qui font confiance à VigIActe.</p>
            </div>

            <div class="flex flex-col items-center gap-[18px] px-6 py-[22px] sm:px-11">
              <IncludedFeatures/>

              <div class="flex w-full max-w-[480px] flex-col items-center gap-2.5">
                <template v-if="canManage">
                  <button type="button"
                          class="cta flex h-[52px] w-full max-w-[420px] items-center justify-center gap-2 rounded-full bg-green-100 text-base font-bold text-white shadow-[0_8px_20px_rgba(30,172,156,.35)]"
                          @click="subscribeFromNotice">
                    Passer en illimité <span class="arw" aria-hidden="true">→</span>
                  </button>
                  <button type="button" class="text-[13px] text-gray-500 hover:text-gray-700" @click="closeTrialEndNotice()">
                    Plus tard, continuer à l'usage ({{ euros(subscription.pricing.usagePricePerDossier) }}&nbsp;€ HT par dossier*)
                  </button>
                  <p class="text-center text-[11px] leading-snug text-gray-400">
                    * Quel que soit le nombre de comparants mis en surveillance et de renouvellements. Le dossier LCB-FT comprend la fiche de vigilance, la DOF et la DBE.
                  </p>
                </template>
                <template v-else>
                  <div class="w-full rounded-xl border border-[#D5F5EF] bg-[#F6FEFC] px-4 py-3 text-center text-sm leading-normal text-gray-800">
                    <template v-if="subscription.viewerRole === 'notaire'">
                      La souscription est faite par le Notaire Administrateur de l'étude<template v-if="administratorName">, <strong>Me {{ administratorName }}</strong></template>.
                    </template>
                    <template v-else>
                      Pour passer en illimité, parlez-en à <strong>{{ administratorName || 'votre Notaire Administrateur' }}</strong>, Notaire Administrateur : lui seul peut souscrire. En attendant, vous pouvez continuer à l'usage.
                    </template>
                  </div>
                  <button type="button"
                          class="cta flex h-[52px] w-full items-center justify-center gap-2 rounded-full bg-green-100 text-base font-bold text-white shadow-[0_8px_20px_rgba(30,172,156,.35)]"
                          @click="plansFromNotice">
                    Voir les formules <span class="arw" aria-hidden="true">→</span>
                  </button>
                  <button type="button" class="text-[13px] text-gray-500 hover:text-gray-700" @click="closeTrialEndNotice()">
                    Continuer à l'usage
                  </button>
                </template>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>
